import { useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase, supabaseConfigured } from "../lib/supabase";

// Magic-link sign-in. Sign-ups are disabled server-side, so only the pre-created user gets in.
export function AuthGate({ children }: { children: (session: Session) => ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (session === undefined) return <Splash />;
  if (!session) return <SignIn />;
  return <>{children(session)}</>;
}

function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <h1 className="text-3xl text-ink-soft">Hearth</h1>
    </div>
  );
}

function SignIn() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState("");

  async function send() {
    setState("sending");
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin + "/" },
    });
    if (error) {
      setState("error");
      setMessage(error.message);
    } else {
      setState("sent");
    }
  }

  return (
    <main className="mx-auto grid min-h-dvh max-w-md content-center gap-6 px-6">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight">Hearth</h1>
        <p className="mt-1 text-ink-soft">The place you return to between quests.</p>
      </div>
      {!supabaseConfigured && (
        <p className="text-sm text-danger">Supabase isn't configured for this build.</p>
      )}
      {state === "sent" ? (
        <div className="card animate-fade-up p-5">
          <p className="font-medium">Check your email.</p>
          <p className="mt-1 text-sm text-ink-soft">
            A sign-in link is on its way to {email}. Open it on this device.
          </p>
        </div>
      ) : (
        <form
          className="card grid gap-3 p-5"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <label className="text-sm text-ink-soft" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            className="field focus:field-focus"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
          <button
            type="submit"
            disabled={state === "sending" || !email}
            className="tap rounded-chip bg-accent px-4 py-2.5 font-semibold text-page transition disabled:opacity-50"
          >
            {state === "sending" ? "Sending…" : "Send sign-in link"}
          </button>
          {state === "error" && <p className="text-sm text-danger">{message}</p>}
        </form>
      )}
    </main>
  );
}
